import { makeStyles } from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IChatMessage } from '../../../libs/models/ChatMessage';
import * as utils from './../../utils/TextUtils';
import { ChatMessageContentRenderer } from './ChatMessageContentRenderer'; // doğru yolu belirt
import { CitationDialog } from './CitationDialog';


const useClasses = makeStyles({
    content: {
        wordBreak: 'break-word',
    },
});

interface ChatHistoryTextContentProps {
    message: IChatMessage;
}

const extractFileNames = (content: string): string => {
    const filePattern = /- ([^\/\s]+\/documents\/[^\/\s]+\/([^\/\s]+?\.(docx|xlsx|pdf|md|txt)))/g;
    return content.replace(filePattern, (_: string, p2: string) => {
        const parts = p2.split('/');
        const fileName = parts[parts.length - 1];
        return `- ${fileName}`;
    });
};

const extractReferences = (content: string) => {
    const refPattern = /<link-start>DocumentId:(.+?);PartitionNumber:(\d+)<link-end>/g;
    let refIndex = 1;
  
    return content.replace(refPattern, (_, documentId, partitionNumber) => {
      const link = `#file:CitationCards.tsx?documentId=${encodeURIComponent(String(documentId))}&partitionNumber=${encodeURIComponent(String(partitionNumber))}`;
      return `[[ ${refIndex++} ]](${link})`;
    });
  };

export const ChatHistoryTextContent: React.FC<ChatHistoryTextContentProps> = ({ message }) => {
    const classes = useClasses();
    let content = utils.replaceCitationLinksWithIndices(utils.formatChatTextContent(message.content), message);
    content = extractFileNames(content);
    content = extractReferences(content); // Process references
    const messageCitations: any[] = message.citations ?? []; //
    
    const [isStreamingComplete, setIsStreamingComplete] = useState(false);
    const [dialogData, setDialogData] = useState<{ documentId: string; partitionNumber: string } | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false); // State to control dialog visibility

    const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault(); // Varsayılan link davranışını engeller
        const href = event.currentTarget.getAttribute('href');
        if (href?.includes('#file:CitationCards.tsx')) {
            const queryString = href.split('#file:CitationCards.tsx?')[1]; // Query string'i ayıkla
            if (queryString) {
                const urlParams = new URLSearchParams(queryString);
                const documentId = urlParams.get('documentId') ?? '';
                const partitionNumber = urlParams.get('partitionNumber') ?? '';
    
                // Dialog verilerini güncelle ve dialogu aç
                setDialogData({ documentId, partitionNumber });
                setIsDialogOpen(true); // Dialogu aç
            }
        }
    };
    

    useEffect(() => {
        const now = Date.now();
        const messageTimestamp = new Date(message.timestamp).getTime(); // Ensure timestamp is in milliseconds

        if (messageTimestamp < now - 2 * 60 * 1000) {
            setIsStreamingComplete(true);
            return undefined; // Explicitly return undefined
        } else if (message.isCompleted) {
            const timer = setTimeout(() => {
                setIsStreamingComplete(true);
            }, 1000); // 1-second delay
            return () => {
                clearTimeout(timer); // Cleanup function
            };
        } else {
            setIsStreamingComplete(false);
            return undefined; // Explicitly return undefined
        }
    }, [message.isCompleted, message.id, message.timestamp]);

    const hasCustomCodeBlock = content.includes('<code-block');

    return (
        <div className={classes.content}>
            {hasCustomCodeBlock && isStreamingComplete ? (
                <ChatMessageContentRenderer content={content} />
            ) : (
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        a: ({ node: _node, ...props }) => {
                            const href = props.href ?? '';
                            return (
                                <a {...props} href={href} target="_blank" rel="noopener noreferrer"         
                                onClick={handleLinkClick}
                                >
                                    {props.children}
                                </a>
                            );
                        },
                    }}
                >
                    {content}
                </ReactMarkdown>
            )}
            {messageCitations.length > 0 && dialogData && (
                <CitationDialog
                    message={message}
                    documentId={dialogData.documentId}
                    partitionNumber={dialogData.partitionNumber}
                    isOpen={isDialogOpen} // Pass the dialog open state
                    onClose={() => {
                        setDialogData(null); // Clear dialog data
                        setIsDialogOpen(false); // Close the dialog
                    }}
                />
            )}
        </div>
    );
};
